import { Table, Tag } from 'react-dsgov';
import { BrButton, BrLoading } from '@govbr-ds/react-components';
import styles from './listagemOperacoes.module.scss';
import { useContext, useEffect, useState } from 'react';
import { formatarNrProcesso } from '../../../../../../../../../utils/formatNrProcesso';
import { formatCurrency } from '../../../../../../../../../utils/formatCurrency';
import classNames from 'classnames';
import { useBackEndRequests } from '../../../../../../../lib/backEndRequests';
import { useQuery } from '@tanstack/react-query';
import {
  IOperacaoOutrosRegimes,
  IOutrosRegimes,
  IRetornoListagemOutrosRegimes,
} from '../../../../../../../../../interfaces/IOperacoes';
import { formatarDataISOpBR } from '../../../../../../../../../utils/formatDate';
import Paginacao from '../../../../../../../../../components/paginacao/paginacao';
import { useNotificacao } from '../../../../../../../../../hooks/useNotificacao';
import titularArrendatarioEstadoProcesso, {
  ETabTitularArrendatario,
  IEstadoTitularArrendatario,
} from '../../../../../../../lib/titularArrendatarioEstadoProcesso';
import { DiefCfemContext } from '../../../../../../../../../contexts/DiefCfemContext';
import { IErrorWithSentryId } from 'interfaces/IErrorWithSentryId';

interface IProps {
  declaracaoId: string;
  setIsEdicaoOutrosRegimes: React.Dispatch<React.SetStateAction<boolean>>;
  estadoTela?: IEstadoTitularArrendatario | null;
  setEstadoTela?: React.Dispatch<React.SetStateAction<IEstadoTitularArrendatario | null>>;
}

interface IHeader {
  field: string;
  label: string;
  format?: Function; //Usa a propriedade da coluna
  value?: string | Function; //Usa toda a linha
}

const ListagemOperacoes = ({ declaracaoId, setIsEdicaoOutrosRegimes, estadoTela, setEstadoTela }: IProps) => {
  const DEFAULT_PAGE_SIZE = 10;
  const PAGE_SIZE_OPTIONS = [3, 5, 10];

  const TEMPLATE_ID_EDIT_BUTTON = 'edit-button_';

  const [listagemRegimes, setListagemRegimes] = useState<IOutrosRegimes[]>([]);

  const context = useContext(DiefCfemContext);

  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageCount, setPageCount] = useState(0);
  const [pageItemCount, setPageItemCount] = useState(0);

  const { getListaOutrosProcessos } = useBackEndRequests();

  const mostrarNotificacao = useNotificacao();

  const {
    data: retornoListagem,
    isLoading,
    isFetching,
    error,
  } = useQuery<IRetornoListagemOutrosRegimes, IErrorWithSentryId>({
    queryKey: ['getListaOutrosProcessos', declaracaoId, pageNumber, pageSize],
    queryFn: () => getListaOutrosProcessos(declaracaoId, pageNumber - 1, pageSize),
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (retornoListagem) {
      setPageItemCount(retornoListagem.page.totalElements);
      setPageCount(retornoListagem.page.totalPages);
      setListagemRegimes(retornoListagem.content);
    } else if ((retornoListagem !== undefined && !retornoListagem) || !!error) {
      const errorResponse = error?.response?.data as any;

      mostrarNotificacao({
        tipo: 'danger',
        conteudo: errorResponse.message || 'Erro ao recuperar os dados.',
        ...(error && { complementoSentryId: error }),
      });
    }
  }, [retornoListagem, error]);

  useEffect(() => {
    if (estadoTela && estadoTela.paginacao?.linkProcesso && !isLoading && !isFetching) {
      const buttonEdit = document.getElementById(estadoTela.paginacao.linkProcesso);

      if (buttonEdit) {
        window.scrollTo({
          top: buttonEdit.offsetTop,
        });
      }
    }
  }, [listagemRegimes, isFetching, isLoading]);

  useEffect(() => {
    carregarEstadoTela();
  }, [estadoTela]);

  const formatarSituacao = (situacao: number) => {
    if (situacao === 1) {
      return (
        <Tag
          data-testid='tag-concluida'
          type='text'
          className={styles.tagTipoSituacao}
          icon='fas fa-check-circle'
          status='success'
          label='Concluída'
        />
      );
    } else {
      return (
        <Tag
          data-testid='tag-pendente'
          type='text'
          className={styles.tagPendente}
          icon='fas fa-exclamation-triangle'
          label='Pendente'
        />
      );
    }
  };

  const formatarBotaoDeEdicao = (situacao: number) => {
    return situacao === 1 ? 'Editar' : 'Iniciar';
  };

  const formatarMunicipio = (declaracao: IOperacaoOutrosRegimes) => {
    return `${declaracao.municipio}/${declaracao.uf}`;
  };

  const formatarBooleano = (valor: boolean) => {
    if (valor === true) {
      return (
        <div className={styles.valorSim}>
          <i className={`fas fa-md ${styles.buttonIcon}`}>&#xf00c;</i> Sim
        </div>
      );
    } else if (valor === false) {
      return (
        <div className={styles.valorNao}>
          <i className={`fas fa-md ${styles.buttonIcon}`}>&#xf00d;</i> Não
        </div>
      );
    } else {
      return <>-</>;
    }
  };

  const formatarOperacao = (operacao: number) => {
    if (operacao == null) {
      return <>-</>;
    } else {
      return formatCurrency(operacao);
    }
  };

  const exibirAcoes = (declaracao: IOperacaoOutrosRegimes) => {
    return (
      <div className={styles.colunaAcoes}>
        <BrButton
          id={TEMPLATE_ID_EDIT_BUTTON + declaracao.declaracaoItemId}
          data-testid={TEMPLATE_ID_EDIT_BUTTON + declaracao.declaracaoItemId}
          onClick={() => editarOperacao(declaracao.declaracaoItemId)}
          title='Editar'
        >
          <i className={`fas fa-edit fa-md  ${styles.columnIcon}`} />
          {formatarBotaoDeEdicao(declaracao.situacao)}
        </BrButton>
      </div>
    );
  };

  const TABELA_CABECALHO: IHeader[] = [
    { field: 'situacao', label: 'Situação', format: formatarSituacao },
    { field: 'declaracaoItemId', label: 'ID Operação' },
    { field: 'substancia', label: 'Substância' },
    { field: 'municipio', label: 'Município/UF', value: formatarMunicipio },
    {
      field: 'houveFatoGerador',
      label: 'Houve Fato Gerador da CFEM?',
      format: formatarBooleano,
    },
    {
      field: 'houveProducao',
      label: 'Houve Produção de Bem Mineral?',
      format: formatarBooleano,
    },
    {
      field: 'valorOperacao',
      label: 'Valor da Operação',
      format: formatarOperacao,
    },
    { field: 'acoes', label: 'Ações', value: exibirAcoes },
  ];

  const carregarEstadoTela = () => {
    if (estadoTela && estadoTela.paginacao) {
      setPageSize(estadoTela.paginacao?.tamanhoPaginacao ?? pageSize);
      setPageNumber(estadoTela.paginacao?.paginaSelecionada ?? pageNumber);
    }
  };

  const atualizarEstadoTela = (id: string) => {
    const novoEstado: IEstadoTitularArrendatario = {
      telaSelecionada: ETabTitularArrendatario.OUTROS_REGIMES_LISTAGEM,
      paginacao: {
        tamanhoPaginacao: pageSize,
        paginaSelecionada: pageNumber,
        linkProcesso: TEMPLATE_ID_EDIT_BUTTON + id,
      },
    };

    if (setEstadoTela) {
      setEstadoTela(novoEstado);
    }

    titularArrendatarioEstadoProcesso.salvarEstado(novoEstado);
  };

  const editarOperacao = (id: string) => {
    atualizarEstadoTela(id);
    context?.setIdOperacaoEditar(id);
    setIsEdicaoOutrosRegimes(true);
  };

  const onPageNumberChange = (number: number) => {
    setPageNumber(number);
  };

  const onPageSizeChange = (size: number) => {
    setPageSize(size);
    setPageNumber(1);
  };

  const htmlInformacao = (titulo: string, conteudo: string, classe?: string) => {
    let label = conteudo ? `${titulo}: ` : titulo;
    return (
      <div
        className={classNames(styles.infoItem, classe ? styles[classe] : '')}
        tabIndex={0}
        aria-label={`${label}${conteudo}`}
      >
        <strong> {label} </strong>
        {conteudo}
      </div>
    );
  };

  const htmlDataVigencia = (dataInicio: string, dataFim: string) => {
    const label = 'Data de Vigência';
    const value = `${dataInicio ? formatarDataISOpBR(dataInicio) : ''} - ${dataFim ? formatarDataISOpBR(dataFim) : ''}`;

    return htmlInformacao(label, value, 'infoVigencia');
  };

  const htmlListaDeclaracao = (processo: IOutrosRegimes, index: number) => {
    return (
      <div className={styles.secaoProcesso} key={index}>
        <div className={styles.info}>
          {htmlInformacao('Processo Minerário N°', formatarNrProcesso(processo.numeroProcesso))}

          {htmlDataVigencia(processo.dataVigenciaInicio, processo.dataVigenciaFim)}
        </div>

        <div className={styles.tabela}>
          <Table
            showTopBar={false}
            headers={TABELA_CABECALHO}
            showPagination={false}
            showDensityButtons={false}
            showSearch={false}
            density='medium'
            tabIndex={0}
          >
            {processo.operacoes?.length === 0 && (
              <tbody className={styles.semRegistro} data-testid='sem-registros-processo'>
                <tr>
                  <td colSpan={TABELA_CABECALHO.length}>Não há registros para serem exibidos.</td>
                </tr>
              </tbody>
            )}

            {processo.operacoes.length > 0 && (
              <tbody data-testid='tabela-processo'>
                {processo.operacoes.map((row: any, index: any) => (
                  <tr key={index}>
                    {TABELA_CABECALHO.map((header) => (
                      <td key={header.field} align='center'>
                        {typeof header.value === 'function' && header.value(row)}

                        {header.format && header.format(row[header.field])}

                        {header.value == null && header.format == null && row[header.field]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </Table>
        </div>
      </div>
    );
  };

  return (
    <div data-testid='situacao-processo'>
      {(isLoading || isFetching) && (
        <div className={styles.loading}>
          <BrLoading large label='Carregando...' />
        </div>
      )}

      {!isLoading && !isFetching && (
        <div className={styles.containerListagem}>
          {listagemRegimes.map((processo: any, index: number) => {
            return htmlListaDeclaracao(processo, index);
          })}
        </div>
      )}

      {listagemRegimes.length === 0 && !isLoading && !isFetching && (
        <div className={styles.semRegistro} data-testid='sem-registros-listagem'>
          Não há registros para serem exibidos.
        </div>
      )}

      {listagemRegimes.length !== 0 && (
        <div className={styles.paginacao}>
          <Paginacao
            pageCount={pageCount}
            itemCount={pageItemCount}
            selectedPage={pageNumber}
            selectedPageSize={pageSize}
            onChangePage={onPageNumberChange}
            onChangeSizePage={onPageSizeChange}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            showPageCombo={true}
          />
        </div>
      )}
    </div>
  );
};

export default ListagemOperacoes;
